import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Check, 
  Star, 
  TrendingUp, 
  Building2, 
  Brain, 
  MapPin, 
  ArrowRight,
  ChevronDown,
  ChevronUp,
  X as LucideX,
  Clock,
  ArrowRight as LucideArrowRight,
  DollarSign,
  User,
  Link as LucideLink,
  BarChart3,
  Mail,
  Calendar,
  Users
} from 'lucide-react';
import { AnimatedNumber } from '../components/AnimatedNumber';
import { WaitlistTopNav } from '../components/WaitlistTopNav';
import { Footer } from '../components/Footer';
import { waitlistService, WaitlistSignup } from '../services/waitlist';

interface PricingPlan {
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  features: string[];
  popular?: boolean;
  cta: string;
}

const pricingPlans: PricingPlan[] = [
  {
    name: 'Free',
    monthlyPrice: 0,
    annualPrice: 0,
    features: [
      'Track 2 properties',
      '5 financial accounts',
      'Basic net worth tracking',
      'Monthly summary',
      'Basic dashboard'
    ],
    cta: 'Join Waitlist'
  },
  {
    name: 'Pro',
    monthlyPrice: 15,
    annualPrice: 96,
    features: [
      'Track 5 properties',
      '10 financial accounts',
      'AI financial insights',
      'Dividend tracking',
      'Real estate ROI analysis',
      'Smart alerts & notifications',
      'Export tools',
      'Advanced analytics'
    ],
    popular: true,
    cta: 'Join Waitlist'
  },
  {
    name: 'Elite',
    monthlyPrice: 29,
    annualPrice: 240,
    features: [
      'Unlimited properties',
      'Unlimited accounts',
      'Advanced dividend tracking & analysis',
      'Business income & expense tracking',
      'Multi-currency support',
      'Advanced real estate analytics',
      'Portfolio rebalancing insights',
      'Custom financial goal tracking'
    ],
    cta: 'Join Waitlist'
  }
];

const testimonials = [
  {
    name: 'Sarah Chen',
    role: 'Real Estate Investor',
    content: 'I can\'t wait for SimpleTracker to launch. The preview I saw was exactly what I need to track my property portfolio.',
    rating: 5
  },
  {
    name: 'Mike Rodriguez',
    role: 'Small Business Owner',
    content: 'Finally, a tool that will make sense of all my finances. The waitlist is worth it for this level of financial intelligence.',
    rating: 5
  },
  {
    name: 'Emily Watson',
    role: 'Financial Advisor',
    content: 'I\'ve seen the beta and it\'s going to revolutionize how Canadians track their wealth. I\'m excited to recommend it to clients.',
    rating: 5
  }
];

const faqs = [
  {
    question: 'When will SimpleTracker launch?',
    answer: 'We\'re working hard to bring SimpleTracker to you soon! Join our waitlist to be among the first to get access and receive exclusive early-bird pricing.'
  },
  {
    question: 'How do I get early access?',
    answer: 'Join our waitlist and we\'ll notify you as soon as we open up beta access. Early waitlist members get priority access and special pricing.'
  },
  {
    question: 'Will there be a free trial?',
    answer: 'Yes! All waitlist members will get a 30-day free trial when we launch, plus exclusive early-bird pricing on our Pro and Elite plans.'
  },
  {
    question: 'Can I cancel my waitlist spot?',
    answer: 'Absolutely. You can unsubscribe from our waitlist emails at any time. No commitment required to join the waitlist.'
  },
  {
    question: 'What makes SimpleTracker different?',
    answer: 'SimpleTracker is built specifically for Canadians with support for all major Canadian banks, real estate ROI tracking, and AI-powered financial insights designed for our unique financial landscape.'
  }
];

const NUMBER_OF_WAITLIST_MEMBERS = 1247;
const AVERAGE_NET_WORTH = 940000; // CAD
const TOTAL_ASSETS_TO_BE_TRACKED = NUMBER_OF_WAITLIST_MEMBERS * AVERAGE_NET_WORTH;

export const WaitlistLandingPage: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleJoinWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!email) {
      setError('Please enter your email address');
      setIsLoading(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    try {
      const signup: WaitlistSignup = {
        email: email.trim().toLowerCase(),
        first_name: firstName.trim() || undefined,
        source: 'landing_page'
      };

      const result = await waitlistService.joinWaitlist(signup);
      
      if (result.success) {
        setIsSubmitted(true);
        // Reset form
        setEmail('');
        setFirstName('');
      } else {
        setError(result.error || 'Failed to join waitlist. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Error joining waitlist:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Animated headline word cycling
  const animatedWords = [
    'confidence',
    'clarity',
    'control',
    'growth',
    'freedom',
  ];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentWordIndex((prev) => (prev + 1) % animatedWords.length);
        setIsAnimating(false);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, [animatedWords.length]);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">You're on the list!</h1>
          <p className="text-gray-600 mb-6">
            Thanks for joining our waitlist! We'll notify you as soon as SimpleTracker launches with exclusive early-bird pricing.
          </p>
          <div className="bg-green-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-800">
              <strong>What's next?</strong><br />
              • Get notified when we launch<br />
              • Receive exclusive early-bird pricing<br />
              • Be among the first to access SimpleTracker
            </p>
          </div>
          <button
            onClick={() => setIsSubmitted(false)}
            className="text-brand-500 hover:text-brand-600 font-medium"
          >
            ← Back to landing page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <WaitlistTopNav />
      {/* Hero Section */}
      <section className="relative bg-white py-10 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="text-left">
              <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-xs sm:text-sm font-medium mb-8">
                <Clock className="w-4 h-4 mr-2" />
                Coming Soon
              </div>
              <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-xs sm:text-sm font-medium mb-8">
                <Users className="w-4 h-4 mr-2" />
                <span style={{ margin: '0 0.25em' }}>
                  <AnimatedNumber value={1247} duration={1200} />
                </span>
                people on waitlist
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                <span className="block text-2xl sm:text-4xl md:text-6xl font-bold text-gray-900 mb-1 leading-tight">Track Your Finances with{' '}</span>
                <span
                  className={`text-brand-500 inline-block transition-all duration-300 ease-in-out ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                  style={{ minWidth: 120, display: 'inline-block' }}
                  aria-live="polite"
                >
                  {animatedWords[currentWordIndex].charAt(0).toUpperCase() + animatedWords[currentWordIndex].slice(1)}
                </span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 max-w-2xl">
                SimpleTracker is coming soon! Join our exclusive waitlist to be among the first to access real-time insights into your net worth, property ROI, and financial goals. 
                No more spreadsheets, just smart financial intelligence.
              </p>
              
              {/* Waitlist Form */}
              <form onSubmit={handleJoinWaitlist} className="mb-8">
                <div className="flex flex-col gap-4 w-full max-w-md">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First name (optional)"
                      className="flex-1 px-4 py-4 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email address *"
                      className="flex-1 px-4 py-4 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="bg-brand-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-brand-600 transition-colors flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Joining...
                      </>
                    ) : (
                      <>
                        Join Waitlist
                        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                  {error && (
                    <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                      {error}
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  🔒 We'll never spam you • 🚀 Be first to launch • 💰 Get early-bird pricing
                </p>
              </form>
              
              {/* Trust indicators */}
              <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-4 sm:gap-8 text-xs sm:text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Bank-level security</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span><AnimatedNumber value={1247} duration={1200} /> waitlist members</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Join the waitlist</span>
                </div>
              </div>
            </div>
            
            {/* Mockup/Demo Section */}
            <div className="relative mt-10 sm:mt-12 lg:mt-0 max-w-xs sm:max-w-md md:max-w-lg mx-auto w-full">
              <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-8 border border-gray-100 w-full">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <div className="text-sm text-gray-500">SimpleTracker Dashboard</div>
                </div>
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-green-50 to-brand-50 rounded-lg p-3 sm:p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-sm text-gray-600">Total Net Worth</div>
                        <div className="text-2xl font-bold text-gray-900">$847,392</div>
                      </div>
                      <div className="text-green-600 text-sm font-medium">+12.4%</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                      <div className="text-xs text-gray-500">Properties</div>
                      <div className="text-lg font-semibold text-gray-900">$650K</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                      <div className="text-xs text-gray-500">Investments</div>
                      <div className="text-lg font-semibold text-gray-900">$197K</div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 bg-orange-100 rounded-lg p-2 sm:p-3 shadow-lg text-xs sm:text-sm">
                <div className="text-xs text-orange-700 font-medium">Coming Soon</div>
                <div className="text-sm text-orange-800">Launch</div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-brand-100 rounded-lg p-2 sm:p-3 shadow-lg text-xs sm:text-sm">
                <div className="text-xs text-brand-700 font-medium">Join Waitlist</div>
                <div className="text-sm text-brand-800">Get Early Access</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section - The Hook */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-4">
              The Problem
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Most Canadians Are Flying Blind With Their Finances
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              You're working hard, saving money, and investing in properties, but you have no clear picture of your true net worth or whether your investments are actually working for you.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-500 shadow-md">
                  <LucideX className="w-7 h-7 text-white" strokeWidth={3} />
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Spreadsheet Hell</h3>
              <p className="text-gray-600 text-center">
                Spending hours updating Excel sheets that are outdated before you finish. One wrong formula and your entire financial picture is wrong.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-500 shadow-md">
                  <LucideX className="w-7 h-7 text-white" strokeWidth={3} />
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">No Real ROI Data</h3>
              <p className="text-gray-600 text-center">
                Your properties might be making money, but you don't know your actual CAP rates, cash-on-cash returns, or total portfolio performance.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-500 shadow-md">
                  <LucideX className="w-7 h-7 text-white" strokeWidth={3} />
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Missing the Big Picture</h3>
              <p className="text-gray-600 text-center">
                You can't see how all your assets work together. Is your real estate portfolio actually growing your wealth faster than the stock market?
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section - The Promise */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-brand-100 text-brand-700 rounded-full text-sm font-medium mb-4">
              The Solution
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              SimpleTracker Is Coming to Give You Crystal Clear Financial Intelligence
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              One dashboard that will show your true net worth, property performance, and AI-powered insights to make smarter financial decisions.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border border-brand-100">
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-500 shadow-md">
                  <Check className="w-7 h-7 text-white" strokeWidth={3} />
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Real-Time Net Worth</h3>
              <p className="text-gray-600 text-center">
                See your exact net worth updated in real-time. No more guessing or outdated spreadsheets. Know exactly where you stand financially.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border border-brand-100">
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-500 shadow-md">
                  <Check className="w-7 h-7 text-white" strokeWidth={3} />
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Property ROI Calculator</h3>
              <p className="text-gray-600 text-center">
                Calculate actual CAP rates, cash-on-cash returns, and total ROI for each property. Know which investments are truly performing.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border border-brand-100">
              <div className="flex justify-center mb-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-500 shadow-md">
                  <Check className="w-7 h-7 text-white" strokeWidth={3} />
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">AI Financial Insights</h3>
              <p className="text-gray-600 text-center">
                Get intelligent recommendations on how to optimize your portfolio, reduce taxes, and reach your financial goals faster.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
              Features
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You'll Need to Track Your Wealth
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful tools designed specifically for Canadian investors and property owners
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <TrendingUp className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Track Everything
              </h3>
              <p className="text-gray-600">
                Monitor all your assets and liabilities in one unified dashboard
              </p>
            </div>
            
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <Building2 className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Property ROI
              </h3>
              <p className="text-gray-600">
                Calculate CAP rates and ROI for your real estate investments
              </p>
            </div>
            
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <Brain className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                AI Insights
              </h3>
              <p className="text-gray-600">
                Get intelligent recommendations and trend analysis
              </p>
            </div>
            
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <svg className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Budgeting
              </h3>
              <p className="text-gray-600">
                Create and track monthly budgets by category. Get alerts when you're approaching limits.
              </p>
            </div>
            
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <svg className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Goal Setting
              </h3>
              <p className="text-gray-600">
                Set and track financial goals like retirement or property purchases with personalized timelines.
              </p>
            </div>
            
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <svg className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Cash Flow Analysis
              </h3>
              <p className="text-gray-600">
                Monitor your monthly income vs expenses. Predict future cash flow and identify potential shortfalls.
              </p>
            </div>
            
            <div className="text-center group hover:transform hover:scale-105 transition-all duration-300">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-200 transition-colors">
                <svg className="w-8 h-8 text-green-600 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 014 0l5 2.857A2 2 0 0122 8.857v6.286a2 2 0 01-1 1.714l-5 2.857a4 4 0 01-4 0l-5-2.857A2 2 0 012 15.143V8.857a2 2 0 011-1.714l5-2.857a4 4 0 014 0z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Data Protection
              </h3>
              <p className="text-gray-600">
                Your data is encrypted and stored offsite. We will never share, sell or rent your personal information. Ever.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
              Simple Setup
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Started in Minutes (When We Launch)
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Three simple steps to start tracking your net worth
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mb-4">
                <User className="w-8 h-8 text-brand-600" />
              </div>
              <div className="text-2xl font-bold text-black mb-1">1</div>
              <div className="font-semibold text-[#4ca585]">Join our waitlist</div>
              <div className="text-gray-600 text-sm">Get notified when we launch with early-bird pricing</div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mb-4">
                <LucideLink className="w-8 h-8 text-brand-600" />
              </div>
              <div className="text-2xl font-bold text-black mb-1">2</div>
              <div className="font-semibold text-[#4ca585]">Get early access</div>
              <div className="text-gray-600 text-sm">Be among the first to try SimpleTracker</div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mb-4">
                <BarChart3 className="w-8 h-8 text-brand-600" />
              </div>
              <div className="text-2xl font-bold text-black mb-1">3</div>
              <div className="font-semibold text-[#4ca585]">See your dashboard</div>
              <div className="text-gray-600 text-sm">Instantly view your net worth and insights</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Banner Section */}
      <section className="py-16" style={{ backgroundColor: '#4ca585' }}>
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8 text-center">
            <div className="text-white">
              <div className="text-3xl md:text-4xl font-bold mb-2">
                <AnimatedNumber value={NUMBER_OF_WAITLIST_MEMBERS} duration={1800} />
              </div>
              <div className="text-brand-100">Waitlist Members</div>
            </div>
            <div className="text-white">
              <div className="text-3xl md:text-4xl font-bold mb-2">
                Coming Soon
              </div>
              <div className="text-brand-100">Launch Date</div>
            </div>
            <div className="text-white">
              <div className="text-3xl md:text-4xl font-bold mb-2">
                4.9★
              </div>
              <div className="text-brand-100">Expected Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-br from-gray-50 to-green-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
              Pricing
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Early-Bird Pricing for Waitlist Members
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Join our waitlist to get exclusive pricing when we launch
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {pricingPlans.map((plan, index) => {
              const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
              const monthlyEquivalent = isAnnual ? plan.annualPrice / 12 : plan.monthlyPrice;
              
              return (
                <div
                  key={plan.name}
                  className={`relative bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:transform hover:-translate-y-2 ${
                    plan.popular ? 'ring-2 ring-brand-500 bg-gradient-to-br from-brand-50 to-white' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-brand-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-gray-900">${price}</span>
                      {plan.monthlyPrice > 0 && (
                        <span className="text-gray-500">
                          {isAnnual ? '/year' : '/month'}
                        </span>
                      )}
                    </div>
                    {isAnnual && plan.monthlyPrice > 0 && (
                      <p className="text-sm text-gray-500">
                        ${monthlyEquivalent.toFixed(0)}/month when billed annually
                      </p>
                    )}
                  </div>
                  
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start group">
                        <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0 group-hover:scale-110 transition-transform" />
                        <span className="text-gray-700 group-hover:text-gray-900 transition-colors">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={() => document.getElementById('waitlist-form')?.scrollIntoView({ behavior: 'smooth' })}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                      plan.popular
                        ? 'bg-brand-500 text-white hover:bg-brand-600'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {plan.cta}
                  </button>
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Early-bird pricing for waitlist members
            </p>
            <p className="text-sm text-gray-500">
              All plans include our core features. Upgrade or downgrade at any time.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about SimpleTracker and our waitlist
            </p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-green-50 transition-colors group"
                >
                  <span className="font-semibold text-gray-900 group-hover:text-green-700">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-green-500 transition-transform" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-green-500 transition-transform" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 border-t border-green-100">
                    <p className="text-gray-700 pt-4">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#4ca585' }}>
        <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-white text-brand-700 rounded-full text-sm font-medium mb-6">
            Limited Time
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Be Among the First to Launch
          </h2>
          <p className="text-xl text-brand-100 mb-6">
            Join <AnimatedNumber value={NUMBER_OF_WAITLIST_MEMBERS} duration={1200} /> Canadians who are waiting for SimpleTracker
          </p>
          
          <div className="bg-white rounded-2xl p-4 sm:p-8 mb-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">What You'll Get:</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 text-left">
              <div className="flex items-start">
                <div className="w-7 h-7 bg-brand-500 rounded-full flex items-center justify-center mr-3 mt-1 shadow-md">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
                <span className="text-gray-700">Early access notification</span>
              </div>
              <div className="flex items-start">
                <div className="w-7 h-7 bg-brand-500 rounded-full flex items-center justify-center mr-3 mt-1 shadow-md">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
                <span className="text-gray-700">Exclusive early-bird pricing</span>
              </div>
              <div className="flex items-start">
                <div className="w-7 h-7 bg-brand-500 rounded-full flex items-center justify-center mr-3 mt-1 shadow-md">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
                <span className="text-gray-700">30-day free trial</span>
              </div>
              <div className="flex items-start">
                <div className="w-7 h-7 bg-brand-500 rounded-full flex items-center justify-center mr-3 mt-1 shadow-md">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
                <span className="text-gray-700">Priority customer support</span>
              </div>
            </div>
          </div>
          
          <form onSubmit={handleJoinWaitlist} id="waitlist-form">
            <div className="flex flex-col gap-4 max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name (optional)"
                  className="flex-1 px-6 py-4 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address *"
                  className="flex-1 px-6 py-4 border border-gray-300 rounded-lg text-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="bg-white text-brand-500 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500 mr-2"></div>
                    Joining...
                  </>
                ) : (
                  <>
                    Join Waitlist
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </button>
              {error && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200">
                  {error}
                </div>
              )}
            </div>
          </form>
          
          <div className="mt-8 space-y-2">
            <p className="text-brand-200 text-sm">
              🔒 Bank-level security • 💰 Early-bird pricing
            </p>
            <p className="text-brand-200 text-xs">
              Join <AnimatedNumber value={NUMBER_OF_WAITLIST_MEMBERS} duration={1200} /> Canadians who are ready to take control of their financial future
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};
